<template>
  <div class="subtitle1 mt-4 mb-4" :data-testid="`${testIdPrefix}-billing-section-title`">
    {{ $t("modals.createWorkspace.billing") }}
  </div>

  <v-text-field
    v-if="!isChildWorkspace"
    :model-value="modelValue.netSuiteId"
    :data-testid="`${testIdPrefix}-net-suite-id`"
    class="mt-3"
    variant="outlined"
    hide-details="auto"
    :rules="[optionalDigitsRule]"
    :label="$t('modals.editWorkspace.netSuiteId')"
    :placeholder="$t('modals.editWorkspace.netSuiteId')"
    @update:model-value="update({ netSuiteId: $event })"
  ></v-text-field>

  <template v-if="isChannelWorkspace">
    <v-text-field
      :model-value="modelValue.netSuiteLegacySubscriptionId"
      :data-testid="`${testIdPrefix}-net-suite-legacy-subscription-id`"
      class="mt-3"
      variant="outlined"
      hide-details="auto"
      :rules="[optionalDigitsRule]"
      :label="$t('modals.editWorkspace.netSuiteLegacySubscriptionId')"
      :placeholder="$t('modals.editWorkspace.netSuiteLegacySubscriptionId')"
      @update:model-value="update({ netSuiteLegacySubscriptionId: $event })"
    ></v-text-field>
    <v-text-field
      :model-value="modelValue.netSuiteSubscriptionId"
      :data-testid="`${testIdPrefix}-new-net-suite-subscription-id`"
      class="mt-3"
      variant="outlined"
      hide-details="auto"
      :rules="[optionalDigitsRule]"
      :label="$t('modals.editWorkspace.newNetSuiteSubscriptionId')"
      :placeholder="$t('modals.editWorkspace.newNetSuiteSubscriptionId')"
      @update:model-value="update({ netSuiteSubscriptionId: $event })"
    ></v-text-field>
  </template>

  <v-text-field
    v-else-if="!isChildWorkspace"
    :model-value="modelValue.netSuiteSubscriptionId"
    :data-testid="`${testIdPrefix}-net-suite-subscription-id`"
    class="mt-3"
    variant="outlined"
    hide-details="auto"
    :rules="[optionalDigitsRule]"
    :label="$t('modals.editWorkspace.netSuiteSubscriptionId')"
    :placeholder="$t('modals.editWorkspace.netSuiteSubscriptionId')"
    @update:model-value="update({ netSuiteSubscriptionId: $event })"
  ></v-text-field>

  <v-switch
    :model-value="modelValue.isNFR"
    :ripple="false"
    class="mt-2"
    density="compact"
    hide-details
    color="primary"
    inset
    flat
    :data-testid="`${testIdPrefix}-nfr-workspace-switch`"
    @update:model-value="onExclusiveArrangementToggle(exclusiveArrangement.NFR, $event)"
  >
    <template #label>
      <div class="body2">
        {{ $t("modals.editWorkspace.nfrWorkspace") }}
      </div>
    </template>
  </v-switch>

  <!--
    ACV is an arrangement negotiated with the MSP that owns the hierarchy, so it only makes
    sense on the root of a channel. Sub-channels inherit their root's arrangement.
  -->
  <v-switch
    v-if="isRootChannelWorkspace"
    :model-value="modelValue.isAcvAccount"
    :ripple="false"
    class="mt-2"
    density="compact"
    hide-details
    color="primary"
    inset
    flat
    :data-testid="`${testIdPrefix}-acv-account-switch`"
    @update:model-value="onExclusiveArrangementToggle(exclusiveArrangement.ACV, $event)"
  >
    <template #label>
      <div class="body2">
        {{ $t("modals.editWorkspace.acvAccount") }}
      </div>
    </template>
  </v-switch>

  <!--
    Ramp is available on any channel, root or sub-channel. `hide-details="auto"` is required
    here and not on the other switches: without it Vuetify keeps the rule in the form's
    validity but never renders its message, leaving Save blocked with no visible reason.
  -->
  <template v-if="isChannelWorkspace">
    <v-switch
      :model-value="modelValue.isRampProgramEnabled"
      :ripple="false"
      class="mt-2"
      density="compact"
      hide-details="auto"
      color="primary"
      inset
      flat
      ref="rampSwitch"
      :rules="[rampPrerequisitesRule]"
      :data-testid="`${testIdPrefix}-ramp-program-switch`"
      @update:model-value="onExclusiveArrangementToggle(exclusiveArrangement.RAMP, $event)"
    >
      <template #label>
        <div class="body2">
          {{ $t("modals.editWorkspace.rampProgram") }}
        </div>
      </template>
    </v-switch>

    <v-text-field
      v-if="modelValue.isRampProgramEnabled"
      :model-value="modelValue.rampProgramCommitmentUnits"
      :data-testid="`${testIdPrefix}-ramp-program-commitment-units`"
      type="number"
      :min="1"
      class="mt-3"
      variant="outlined"
      hide-details="auto"
      :rules="[numberRequired(), onlyInteger()]"
      :label="$t('modals.editWorkspace.minimumCommitment')"
      :placeholder="$t('modals.editWorkspace.minimumCommitment')"
      @update:model-value="onCommitmentUnitsInput"
    ></v-text-field>
  </template>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType, ref, watch } from "vue";
import { ExclusiveBillingArrangement, WorkspaceType } from "@/constants/workplaces";
import { useI18n } from "vue-i18n";
import { Patterns } from "@coronet/cui";
import { numberRequired, onlyInteger } from "@/_helpers/validators";
import type { WorkspaceBillingFields } from "@/_store/msp/msp.module";
import type { VSwitch } from "vuetify/components";

// The arrangements gated behind `isRootChannelWorkspace` in the template. NFR applies to every
// workspace type and Ramp to every channel, so neither belongs here. Keep this in step with
// that `v-if`.
const ROOT_ONLY_ARRANGEMENTS = [ExclusiveBillingArrangement.ACV];

export default defineComponent({
  props: {
    modelValue: {
      type: Object as PropType<WorkspaceBillingFields>,
      required: true,
    },
    workspaceType: {
      type: String as PropType<WorkspaceType>,
      required: true,
    },
    parentWorkspaceId: {
      type: String as PropType<string | undefined>,
      default: undefined,
    },
    testIdPrefix: {
      type: String,
      required: true,
    },
  },
  emits: ["update:modelValue"],
  setup(props, { emit }) {
    const isChannelWorkspace = computed(() => props.workspaceType === WorkspaceType.CHANNEL);

    const isChildWorkspace = computed(() => props.workspaceType === WorkspaceType.CHILD);

    const isRootChannelWorkspace = computed(
      () => isChannelWorkspace.value && !props.parentWorkspaceId
    );

    const { t } = useI18n();

    const optionalDigitsRule = (value: string) => {
      if (!value) return true;
      if (!Patterns.ONLY_DIGITS.test(value)) return t("validations.onlyDigits");
      return true;
    };

    // Ramp billing is reconciled against a NetSuite subscription, so the identifiers have to be
    // in place before the program can be switched on. The rule stays live for an already
    // enabled workspace, so clearing the identifiers later blocks the save too.
    const rampPrerequisitesRule = (enabled: boolean | null) => {
      if (!enabled) return true;

      const hasSubscriptionId =
        !!props.modelValue.netSuiteSubscriptionId ||
        !!props.modelValue.netSuiteLegacySubscriptionId;

      return props.modelValue.netSuiteId && hasSubscriptionId
        ? true
        : t("validations.rampRequiresNetSuiteIds");
    };

    // Vuetify only re-runs a field's rules when that field's own value changes, so filling the
    // NetSuite inputs leaves the switch showing a stale error. Re-validate it by hand whenever
    // the values the rule reads change.
    const rampSwitch = ref<InstanceType<typeof VSwitch> | null>(null);

    watch(
      () => [
        props.modelValue.netSuiteId,
        props.modelValue.netSuiteSubscriptionId,
        props.modelValue.netSuiteLegacySubscriptionId,
      ],
      () => {
        if (props.modelValue.isRampProgramEnabled) rampSwitch.value?.validate();
      }
    );

    // Spreading the incoming model is what keeps this component safe to bind a whole workspace
    // item to: anything it does not know about survives the round trip untouched.
    const update = (changes: WorkspaceBillingFields) => {
      emit("update:modelValue", { ...props.modelValue, ...changes });
    };

    // Ramp owns the commitment field, so switching it off has to drop the units with it.
    const clearArrangement = (arrangement: ExclusiveBillingArrangement): WorkspaceBillingFields =>
      arrangement === ExclusiveBillingArrangement.RAMP
        ? { [arrangement]: false, rampProgramCommitmentUnits: undefined }
        : { [arrangement]: false };

    // The commitment is an Integer on the backend, so an emptied field has to clear it
    // rather than fall back to 0, which would read as a real commitment of zero units.
    const onCommitmentUnitsInput = (value: string) => {
      const parsed = Number(value);
      update({
        rampProgramCommitmentUnits: value === "" || Number.isNaN(parsed) ? undefined : parsed,
      });
    };

    // NFR, ACV, Pay-in-advance and Ramp are four ways of billing the same MSP, so enabling
    // one has to switch the other three off - in a single update, not one emit per flag.
    const onExclusiveArrangementToggle = (
      arrangement: ExclusiveBillingArrangement,
      value: boolean | null
    ) => {
      if (!value) {
        update(clearArrangement(arrangement));
        return;
      }

      const changes: WorkspaceBillingFields = { [arrangement]: true };
      Object.values(ExclusiveBillingArrangement)
        .filter((other) => other !== arrangement && props.modelValue[other])
        .forEach((other) => Object.assign(changes, clearArrangement(other)));

      update(changes);
    };

    // Picking a parent turns a channel into a sub-channel mid-form, so drop any
    // root-only arrangement the user had already switched on before that point.
    watch(isRootChannelWorkspace, (isRootChannel) => {
      if (isRootChannel) return;

      const changes: WorkspaceBillingFields = {};
      ROOT_ONLY_ARRANGEMENTS.filter((arrangement) => props.modelValue[arrangement]).forEach(
        (arrangement) => Object.assign(changes, clearArrangement(arrangement))
      );

      if (Object.keys(changes).length) update(changes);
    });

    return {
      exclusiveArrangement: ExclusiveBillingArrangement,
      isChannelWorkspace,
      isChildWorkspace,
      isRootChannelWorkspace,
      optionalDigitsRule,
      rampPrerequisitesRule,
      rampSwitch,
      update,
      onCommitmentUnitsInput,
      onExclusiveArrangementToggle,
      numberRequired,
      onlyInteger,
    };
  },
});
</script>
